import { groupBy } from 'lodash'
import { Discounts } from './Discounts'
import { OrderParams, OrderPriceSummary } from '../summary/getOrderPriceSummary'
import {
  DiscountConfiguration,
  OrderDiscountConfiguration,
  VariableDiscountConfiguration,
} from '@ca/common/types'
import { applyDiscountsToElements } from './applyDiscountsToElements'
import { validateOrderValues } from '../checkOrderValues'

export const getDiscountedElements = (
  item: OrderPriceSummary['items'][number],
  variableDiscounts: VariableDiscountConfiguration[] = [],
  orderDiscounts: OrderDiscountConfiguration[] = []
) => {
  const result = new Map(item.elements)

  const bundleType = item.item.bundleType

  const isBundleMatched = (discount: DiscountConfiguration) => {
    if (!discount.bundle) {
      return true
    }
    return discount.bundle === bundleType
  }

  const nonFixedOrderDiscounts = orderDiscounts.filter(
    (discount) => discount.type !== 'fixed' && isBundleMatched(discount)
  )

  result.forEach((element) => {
    const withOrderDiscount = Discounts.applyDiscounts(
      element.price,
      nonFixedOrderDiscounts
    )

    result.set(element.variable, {
      ...element,
      price: withOrderDiscount,
    })
  })

  return applyDiscountsToElements(
    result,
    variableDiscounts.filter(isBundleMatched)
  )
}

export type DiscountsByScope = {
  order?: OrderDiscountConfiguration[]
  variable?: VariableDiscountConfiguration[]
}

/**
 * Applies discounts to the order price summary top down (where applicable):
 * 1. Apply order percentage discounts to the elements of each item
 * 2. Apply variable discounts
 * 3. Apply fixed order discounts to the order
 */
export const topDownStrategy = (
  orderPrice: OrderPriceSummary,
  discounts: DiscountConfiguration[],
  params: OrderParams
): OrderPriceSummary => {
  const discountsByScope = groupBy(discounts, 'scope') as DiscountsByScope

  const items = orderPrice.items.map((item) => {
    const elements = getDiscountedElements(
      item,
      discountsByScope.variable?.filter(
        (discount) =>
          !discount.condition ||
          validateOrderValues(
            params.config.conditions[discount.condition],
            item.values,
            params.config
          )?.result
      ),
      discountsByScope.order
    )

    const total = Discounts.sumItemsWithPrices(elements.values())

    return {
      id: item.id,
      price: total,
      item: item.item,
      values: item.values,
      elements,
    }
  })

  const sum = Discounts.sumItemsWithPrices(items)

  const price = Discounts.applyDiscountsByType(
    sum,
    discountsByScope.order,
    'fixed'
  )

  return {
    price,
    items,
  }
}
